import { ECatalogItemIdentifierType } from '@/core/apis/config_api/config_api_types';
import { IS_DEBUG } from '@/core/constants/appConfig/environment';
import { MARKET_FILTERING_ENABLED } from '@/core/constants/appConfig/types';
import {
  EMableCustomEventName,
  EMableEventName,
} from '@/core/constants/event_types/events';
import { MarketDataHandler } from '@/core/data_handlers/market';
import { EDestinationType } from '@/core/interfaces';
import { GoogleAdsWrapper } from '@/destinations/shared/gtag/tag_manager_wrapper/gads_wrapper';
import { TGtagUserData } from '@/destinations/shared/gtag/tag_manager_wrapper/types';
import { processGoogleUserData } from '@/destinations/shared/gtag/tag_manager_wrapper/user_data_helper';
import { DestinationStatusHandler } from '@/destinations/status';
import { Logger } from '@/shared/logger/logger';

import { DestinationBundle, DestinationSendingResult } from '../abstract';
import { GoogleAdsPayloadManager } from './composer/google_ads_payload_manager';
import {
  calculateGAdsEventMapping,
  GAdsConversionPayloadWithoutTransactionId,
  GAdsConversionPayloadWithTransactionId,
  GadsPayloadEventName,
  GoogleAdsDestinationPayload,
  TSupportedGadsEventNames,
} from './google_ads_types';

import { TMableEvent } from '../../../core/constants/event_types/base_mable_event';
import { TGadsDestinationCredentials } from '../../types/destination_creds_types';

/**
 * Get the market ID for an event
 * @param mableEvent - The mable event
 * @param primaryMarketID - The primary market ID
 * @returns The market ID for the event
 */
function getMarketIDForEvent(
  mableEvent: TMableEvent,
  primaryMarketID: string,
): string | undefined {
  if (mableEvent?.sd?.mid?.mid) {
    return mableEvent.sd.mid.mid;
  }
  // If no market ID is found, then return the primary market ID
  return primaryMarketID;
}

/*
 * Special Functionality:
 * Only send conversion if event is present in conversion map
 * Always send remarketing event if they are not disabled
 * Special logic to calculate if network request went out or not is required
 */

export class GoogleAdsDestinationBundle extends DestinationBundle {
  override allowedEvents: EMableEventName[] = [
    EMableEventName.PAGE_VIEWED,
    EMableEventName.PRODUCT_ADDED,
    EMableEventName.CHECKOUT_STARTED,
    EMableEventName.PAYMENT_INFO_ADDED,
    EMableEventName.PRODUCT_VIEWED,
    EMableEventName.LEAD,
    // EMableEventName.COLLECTION_VIEWED,
    EMableEventName.ORDER_COMPLETED,
    EMableEventName.CUSTOM_EVENT,
  ];

  private readonly googleAdsWrapper: GoogleAdsWrapper;
  _remarketingEventsDisabled: boolean;
  static eEventNameToGadsPayloadMap: Record<
    TSupportedGadsEventNames,
    GadsPayloadEventName
  > = {
    [EMableCustomEventName.AllCustomerPurchase]:
      GadsPayloadEventName.CUSTOM_ALL_CUSTOMER_PURCHASE,
    [EMableCustomEventName.NewCustomerPurchase]:
      GadsPayloadEventName.CUSTOM_NEW_CUSTOMER_PURCHASE,
    [EMableCustomEventName.ReturningCustomerPurchase]:
      GadsPayloadEventName.CUSTOM_RETURNING_CUSTOMER_PURCHASE,
    [EMableEventName.CHECKOUT_STARTED]: GadsPayloadEventName.BEGIN_CHECKOUT,
    [EMableEventName.COLLECTION_VIEWED]: GadsPayloadEventName.VIEW_ITEM_LIST,
    [EMableEventName.CUSTOM_EVENT]: GadsPayloadEventName.CUSTOM,
    [EMableEventName.LEAD]: GadsPayloadEventName.LEAD,
    [EMableEventName.ORDER_COMPLETED]: GadsPayloadEventName.PURCHASE,
    [EMableEventName.PAGE_VIEWED]: GadsPayloadEventName.PAGE_VIEW,
    [EMableEventName.PAYMENT_INFO_ADDED]: GadsPayloadEventName.ADD_PAYMENT_INFO,
    [EMableEventName.PRODUCT_ADDED]: GadsPayloadEventName.ADD_TO_CART,
    [EMableEventName.PRODUCT_VIEWED]: GadsPayloadEventName.VIEW_ITEM,
  };
  gtagStatus!: any;
  override hasBrowserDestination = true;

  /**
   * Get the conversion ID for an event
   * @param eventName - The event name
   * @param marketID - The market ID
   * @returns The conversion ID for the event
   */
  getConversionIDForEvent(
    eventName: GadsPayloadEventName,
    marketID?: string,
  ): string | undefined {
    /**
     * If market filtering is enabled and per market is enabled, then we need to get the conversion ID for the event
     * based on the market ID
     */
    if (
      MARKET_FILTERING_ENABLED &&
      this.destinationConfig['isPerMarket'] &&
      marketID
    ) {
      // Get the conversion ID for the event
      // If Market ID is not provided then we filter out the event
      return (
        this.destinationConfig['marketConversionEventsMap'] as Record<
          string,
          Record<GadsPayloadEventName, string>
        >
      )?.[marketID]?.[eventName];
    }
    // If per market is not enabled, then use the conversion events map
    return (
      this.destinationConfig['conversionEventsMap'] as Record<
        GadsPayloadEventName,
        string
      >
    )?.[eventName];
  }

  constructor(
    destinationSetting: TGadsDestinationCredentials,
    destinationId: string,
  ) {
    // Ensure catalog_item_identifier_type is set
    if (!destinationSetting.catalog_item_identifier_type) {
      destinationSetting.catalog_item_identifier_type =
        ECatalogItemIdentifierType.PRODUCT_ID;
    }

    super(EDestinationType.GoogleAds, destinationSetting, destinationId);
    this._remarketingEventsDisabled = false;
    if (!this.destinationConfig['adwordsId']) {
      throw new Error(
        'GoogleAdsDestinationBundle: Missing required configuration parameters: adwordsId, ',
      );
    }
    if (this.destinationConfig['remarketingEventsDisabled']) {
      this._remarketingEventsDisabled = true;
    }
    this.gtagStatus = new DestinationStatusHandler({
      destinationUrl: 'https://www.google.com/pagead/1p-user-list/123456',
      storageNamespace: 'googleAds(Status)',
    });
    this.googleAdsWrapper = new GoogleAdsWrapper();
  }

  override getBrowserDestinationSendingStatus(): boolean {
    return this.gtagStatus?.isAvailable || false;
  }

  override async init(): Promise<void> {
    this.gtagStatus = await this.gtagStatus.getStatus();
    try {
      this.googleAdsWrapper.initGoogleAds(
        this.destinationConfig['adwordsId'],
        '',
        {
          debug_mode: IS_DEBUG,
        },
      );
    } catch (error) {
      Logger.error(
        'GoogleAdsDestinationBundle: Error initializing Google Ads destination',
        error,
      );
    }
  }

  override async calculatePayload(
    mableEvent: TMableEvent,
  ): Promise<GoogleAdsDestinationPayload> {
    const eventName: EMableEventName = mableEvent.en;
    const mappedEventName: GadsPayloadEventName = calculateGAdsEventMapping(
      eventName,
      mableEvent?.esd?.cen as EMableCustomEventName | undefined,
    );
    const event_params = GoogleAdsPayloadManager.calculatePayload({
      mableEvent,
    });
    const googleAdsUserData: TGtagUserData = await processGoogleUserData(
      mableEvent.cdas,
    );
    let payload: Partial<GoogleAdsDestinationPayload> = {
      event_name: mappedEventName,
      event_payload: event_params as any,
      gads_user_data: googleAdsUserData,
      marketID: getMarketIDForEvent(
        mableEvent,
        MarketDataHandler.primaryMarketID,
      ),
    };
    return payload as GoogleAdsDestinationPayload;
  }

  override async sendToDestination(
    payload: GoogleAdsDestinationPayload,
  ): Promise<DestinationSendingResult> {
    const event_name = payload.event_name;
    const conversionLabel: string | undefined = this.getConversionIDForEvent(
      event_name,
      payload?.marketID,
    );

    if (!conversionLabel) {
      return {
        destinationReqEvent: {},
        reason: `No conversion ID found for event ${event_name} in market ${payload?.marketID}`,
        success: false,
      };
    }

    // Add Google Ads Specific User Data ( for enhanced conversion events )
    this.googleAdsWrapper.setUserData(payload.gads_user_data);

    const send_to = `${this.destinationConfig['adwordsId']}/${conversionLabel}`;
    if (conversionLabel) {
      if (event_name === GadsPayloadEventName.PURCHASE) {
        this.googleAdsWrapper.trackConversion(
          send_to,
          payload.event_payload as GAdsConversionPayloadWithTransactionId,
        );
      } else {
        this.googleAdsWrapper.trackConversion(
          send_to,
          payload.event_payload as GAdsConversionPayloadWithoutTransactionId,
        );
      }
    }
    if (!this._remarketingEventsDisabled) {
      this.googleAdsWrapper.trackEvent(event_name, '', payload.event_payload);
      return {
        destinationReqEvent: {},
        reason: 'Payload successfully sent',
        success: true,
      };
    }
    return {
      destinationReqEvent: {},
      reason: 'Payload successfully sent',
      success: true,
    };
  }
}
